import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Calculator, AlertTriangle, RotateCcw } from 'lucide-react';

export default function VolumetricCalculator() {
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');

  const l = parseFloat(length) || 0;
  const w = parseFloat(width) || 0;
  const h = parseFloat(height) || 0;
  const hasInput = l > 0 && w > 0 && h > 0;
  const volumetricWeight = hasInput ? (l * w * h) / 5000 : 0;
  const isOverLimit = volumetricWeight > 50;

  const reset = () => {
    setLength('');
    setWidth('');
    setHeight('');
  };

  return (
    <div className="space-y-6" data-testid="volumetric-calculator-page">
      <div>
        <h1 className="text-3xl font-black text-primary flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Calculator className="h-6 w-6 text-primary" />
          </span>
          حاسبة الوزن الحجمي
        </h1>
        <p className="text-foreground/70 mt-2">
          احسب الوزن الحجمي للطرد — المعادلة: الطول × العرض × الارتفاع ÷ 5000
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 md:p-8 border-border">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Calculator className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-primary">أبعاد الطرد (سم)</h2>
              <p className="text-sm text-foreground/70">أدخل الأبعاد بالسنتيمتر</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="length" className="font-bold">الطول (سم)</Label>
              <Input
                id="length"
                data-testid="length-input"
                type="number"
                step="0.01"
                min="0"
                placeholder="0"
                value={length}
                onChange={(e) => setLength(e.target.value)}
                className="text-right h-12 text-lg font-numbers"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="width" className="font-bold">العرض (سم)</Label>
              <Input
                id="width"
                data-testid="width-input"
                type="number"
                step="0.01"
                min="0"
                placeholder="0"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                className="text-right h-12 text-lg font-numbers"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="height" className="font-bold">الارتفاع (سم)</Label>
              <Input
                id="height"
                data-testid="height-input"
                type="number"
                step="0.01"
                min="0"
                placeholder="0"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                className="text-right h-12 text-lg font-numbers"
              />
            </div>

            <Button
              onClick={reset}
              variant="outline"
              data-testid="reset-button"
              className="w-full gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              إعادة تعيين
            </Button>
          </div>
        </Card>

        <Card className={`p-8 border-2 transition-all ${
          !hasInput ? 'border-border bg-muted/30'
            : isOverLimit ? 'border-destructive bg-destructive/5'
            : 'border-success bg-success/5'
        }`}>
          <div className="flex flex-col h-full justify-center items-center text-center space-y-6">
            <div>
              <p className="text-sm font-bold text-muted-foreground uppercase tracking-wider">
                الوزن الحجمي
              </p>
              <p
                data-testid="volumetric-result"
                className={`text-6xl md:text-7xl font-black font-numbers mt-4 ${
                  !hasInput ? 'text-muted-foreground'
                    : isOverLimit ? 'text-destructive'
                    : 'text-success'
                }`}
              >
                {volumetricWeight.toFixed(2)}
              </p>
              <p className="text-2xl font-bold mt-2">kg</p>
            </div>

            {hasInput && (
              <div className="w-full space-y-2 pt-4 border-t border-border/50">
                <p className="text-sm text-muted-foreground">
                  {l} × {w} × {h} ÷ 5000
                </p>
              </div>
            )}

            {isOverLimit && (
              <div
                data-testid="over-limit-warning"
                className="w-full p-4 rounded-lg bg-destructive text-destructive-foreground flex items-center gap-3"
              >
                <AlertTriangle className="h-6 w-6 flex-shrink-0" />
                <p className="font-bold text-right flex-1">
                  لا يُسمح بالوزن الحجمي أكثر من 50kg
                </p>
              </div>
            )}

            {hasInput && !isOverLimit && (
              <div className="w-full p-3 rounded-lg bg-success/10 text-success text-sm font-bold">
                ✓ ضمن الحد المسموح به
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card className="p-6 border-border bg-accent/20">
        <h3 className="text-lg font-bold mb-3">معادلة الحساب</h3>
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>• الوزن الحجمي (kg) = الطول × العرض × الارتفاع ÷ 5000</p>
          <p>• جميع الأبعاد بالسنتيمتر (سم)</p>
          <p>• الحد الأقصى المسموح: 50 kg</p>
        </div>
      </Card>
    </div>
  );
}
